import React, { useState } from 'react';
import Modal from '../common/Modal';
import { OAuthButton } from './OAuthButton';
import { AccountRecoveryForm } from './AccountRecoveryForm';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import Button from '../common/Button';

const MIN_AGE_YEARS = 18;

function isAgeAtLeast18(dobStr: string): boolean {
  const dob = new Date(dobStr);
  const today = new Date();
  const threshold = new Date(today.getFullYear() - MIN_AGE_YEARS, today.getMonth(), today.getDate());
  return dob <= threshold;
}

function maxDobDate(): string {
  const today = new Date();
  const max = new Date(today.getFullYear() - MIN_AGE_YEARS, today.getMonth(), today.getDate());
  return max.toISOString().split('T')[0];
}

interface LoginModalProps {
  isOpen: boolean;
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen }) => {
  const [step, setStep] = useState<'login' | 'guest-dob' | 'recovery'>('login');
  const [dob, setDob] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [ageError, setAgeError] = useState<string | null>(null);
  const { loginAsGuest, isLoading } = useAuth();
  const authNotice = useAuthStore((s) => s.authNotice);
  const setAuthNotice = useAuthStore((s) => s.setAuthNotice);

  // The notice explains how the player got back here; dismiss it as soon as they act on it.
  const goToStep = (next: 'login' | 'guest-dob' | 'recovery') => {
    setAuthNotice(null);
    setAgeError(null);
    setStep(next);
  };

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dob) {
      setAgeError('Please enter your date of birth.');
      return;
    }
    if (!isAgeAtLeast18(dob)) {
      setAgeError('You must be 18 or older to play HexCrawl.');
      return;
    }
    if (!tosAccepted) {
      setAgeError('You must accept the Privacy Policy to continue.');
      return;
    }
    setAgeError(null);
    try {
      await loginAsGuest(new Date(dob));
    } catch {
      setAgeError('Failed to create guest account. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} className="login-modal">
      <div className="text-white">
        <h2 style={{ textAlign: 'center' }} className="text-2xl font-bold mb-4">Welcome to HexCrawl</h2>

        {authNotice && (
          <div
            role="alert"
            style={{
              marginBottom: '12px',
              padding: '10px',
              borderRadius: '4px',
              border: `1px solid ${authNotice.kind === 'success' ? '#3a7d44' : '#a33'}`,
              background: authNotice.kind === 'success'
                ? 'rgba(58, 125, 68, 0.15)'
                : 'rgba(170, 51, 51, 0.15)',
              color: authNotice.kind === 'success' ? '#a8e6b0' : '#ffb3b3',
              fontSize: '13px',
            }}
          >
            {authNotice.text}
          </div>
        )}

        {step === 'login' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <OAuthButton provider="discord" />
            <OAuthButton provider="google" />
            <Button
              onClick={() => goToStep('recovery')}
              style={{
                padding: '10px 16px',
                background: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxSizing: 'border-box',
              }}
            >
              <span style={{ width: '20px', textAlign: 'center', fontSize: '18px' }} aria-hidden="true">✉️</span>
              <span style={{ width: '100%' }}>Email me a login link</span>
            </Button>
            <Button
              onClick={() => goToStep('guest-dob')}
              style={{
                padding: '10px 16px',
                background: '#FFFFFF',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginTop: '8px',
              }}
            >
              Play as Guest
            </Button>
          </div>
        )}

        {step === 'guest-dob' && (
          <form onSubmit={handleGuestSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '14px', color: '#ccc', textAlign: 'center' }}>
              HexCrawl uses AI-generated content. You must be 18 or older to play.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label htmlFor="dob" style={{ fontSize: '13px', color: '#aaa' }}>Date of Birth</label>
              <input
                id="dob"
                type="date"
                value={dob}
                max={maxDobDate()}
                onChange={e => setDob(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #555',
                  background: '#222',
                  color: 'white',
                  fontSize: '14px',
                  colorScheme: 'dark',
                }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#ccc', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={tosAccepted}
                onChange={e => setTosAccepted(e.target.checked)}
                style={{ marginTop: '2px', flexShrink: 0 }}
              />
              I confirm I am 18 or older and agree to the{' '}
              <a href="https://hexcrawl.online/privacy" target="_blank" rel="noreferrer" style={{ color: '#7289DA' }}>
                Privacy Policy
              </a>
              .
            </label>

            {ageError && (
              <p style={{ color: '#ff6b6b', fontSize: '13px', margin: 0 }}>{ageError}</p>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                onClick={() => goToStep('login')}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Back
              </Button>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  flex: 2,
                  padding: '10px',
                  background: '#FFFFFF',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {isLoading ? 'Creating Account...' : 'Play as Guest'}
              </button>
            </div>
          </form>
        )}

        {step === 'recovery' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '14px', color: '#ccc', textAlign: 'center' }}>
              Enter the email address on your account and we'll send you a link to sign in.
            </p>

            <AccountRecoveryForm />

            <Button
              onClick={() => goToStep('login')}
              style={{
                padding: '10px',
                background: '#444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Back
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
